import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { FileCheck, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { material: '', lot_number: '', batch: '', certificate_type: 'Certificate of Analysis', valid_from: today(), valid_to: '', results_summary: '', issued_by: '' };

const QualityCertificates = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('quality_certificate');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/quality/certificates`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load certificates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/quality/certificates`, {
        ...form, lot_number: form.lot_number || null, batch: form.batch || null,
        valid_to: form.valid_to || null, issued_by: form.issued_by || null,
      }, { withCredentials: true });
      toast.success('Certificate issued'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to issue certificate'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="qm-certificates-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><FileCheck className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quality Certificates</h1>
              <p className="text-gray-600 text-sm mt-0.5">Certificates of analysis / conformance.</p>
            </div>
          </div>
          {writable && <Button onClick={() => setOpen(true)} data-testid="new-certificate-btn"><Plus className="h-4 w-4 mr-2" />New Certificate</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Certificate #</TableHead><TableHead>Material</TableHead><TableHead>Type</TableHead>
              <TableHead>Lot</TableHead><TableHead>Valid From</TableHead><TableHead>Valid To</TableHead><TableHead>Issued By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No certificates yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.certificate_number} data-testid={`cert-row-${r.certificate_number}`}>
                    <TableCell className="font-medium">{r.certificate_number}</TableCell>
                    <TableCell>{r.material}</TableCell>
                    <TableCell>{r.certificate_type}</TableCell>
                    <TableCell>{r.lot_number || '—'}</TableCell>
                    <TableCell>{r.valid_from || '—'}</TableCell>
                    <TableCell>{r.valid_to || '—'}</TableCell>
                    <TableCell>{r.issued_by || '—'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Quality Certificate</DialogTitle>
            <DialogDescription>Issue a certificate of analysis for a material / lot.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label>Material</Label><Input data-testid="cert-material" value={form.material} onChange={(e) => set('material', e.target.value)} /></div>
            <div><Label>Certificate Type</Label><Input data-testid="cert-type" value={form.certificate_type} onChange={(e) => set('certificate_type', e.target.value)} /></div>
            <div><Label>Lot Number</Label><Input data-testid="cert-lot" value={form.lot_number} onChange={(e) => set('lot_number', e.target.value)} /></div>
            <div><Label>Batch</Label><Input data-testid="cert-batch" value={form.batch} onChange={(e) => set('batch', e.target.value)} /></div>
            <div><Label>Valid From</Label><Input type="date" data-testid="cert-from" value={form.valid_from} onChange={(e) => set('valid_from', e.target.value)} /></div>
            <div><Label>Valid To</Label><Input type="date" data-testid="cert-to" value={form.valid_to} onChange={(e) => set('valid_to', e.target.value)} /></div>
            <div><Label>Issued By</Label><Input data-testid="cert-issued-by" value={form.issued_by} onChange={(e) => set('issued_by', e.target.value)} /></div>
            <div className="col-span-2"><Label>Results Summary</Label><Textarea data-testid="cert-summary" value={form.results_summary} onChange={(e) => set('results_summary', e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.material} data-testid="save-certificate-btn">{saving ? 'Saving...' : 'Issue Certificate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default QualityCertificates;
